import React from 'react';
import { Scale, Users, Building2, Briefcase, Globe, GraduationCap, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';

// We'll implement ServiceCard directly in this file to ensure visual consistency
const ServiceCard = ({ icon, title, description, link, color }) => {
  return (
    <Link 
      to={link}
      className="group bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-lg transition-all duration-300 transform hover:-translate-y-2 block"
    >
      <div className="relative overflow-hidden">
        <div className="absolute top-0 left-0 h-full w-1 bg-gray-200 transition-all duration-300 ease-in-out group-hover:w-full group-hover:bg-opacity-10 z-0"></div>
        <div className={`absolute top-0 left-0 h-1 w-0 ${color} transition-all duration-500 ease-in-out group-hover:w-full z-10`}></div>
        
        <div className="p-8 relative z-10">
          <div className={`${color.replace('group-hover:', '')} bg-opacity-10 p-4 inline-flex rounded-lg mb-6 transition-colors duration-300`}>
            {icon}
          </div>
          <h3 className="text-xl font-bold mb-3 group-hover:text-indigo-700 transition-colors">{title}</h3>
          <p className="text-gray-600 mb-6">{description}</p>
          
          <div className="flex items-center text-gray-400 group-hover:text-indigo-600 transition-colors">
            <span className="mr-2 font-medium">Learn more</span>
            <ArrowRight className="w-4 h-4 transform group-hover:translate-x-1 transition-transform" />
          </div>
        </div>
      </div>
    </Link>
  );
};

const Services = () => {
  const services = [
    {
      icon: <Users className="w-12 h-12" />,
      title: 'Family Law and Court Marriage',
      description: 'Compassionate legal support for family matters including divorce, custody, and domestic relations.',
      link: '/services/family-law',
      color: 'group-hover:bg-blue-600',
      bgColor: 'bg-blue-600'
    },
    {
      icon: <Scale className="w-12 h-12" />,
      title: 'Criminal Law',
      description: 'Expert defense in criminal cases with a focus on protecting your rights and ensuring fair treatment.',
      link: '/services/criminal-law',
      color: 'group-hover:bg-red-600',
      bgColor: 'bg-red-600'
    },
    {
      icon: <Building2 className="w-12 h-12" />,
      title: 'Civil Law',
      description: 'Comprehensive civil litigation services covering property disputes and contractual matters.',
      link: '/services/civil-law',
      color: 'group-hover:bg-green-600',
      bgColor: 'bg-green-600'
    },
    {
      icon: <Briefcase className="w-12 h-12" />,
      title: 'Business Law',
      description: 'Strategic legal solutions for businesses, including corporate law and commercial transactions.',
      link: '/services/business-law',
      color: 'group-hover:bg-purple-600',
      bgColor: 'bg-purple-600'
    },
    {
      icon: <Globe className="w-12 h-12" />,
      title: 'International Law',
      description: 'Expert guidance in international legal matters and cross-border disputes.',
      link: '/services/international-law',
      color: 'group-hover:bg-indigo-600',
      bgColor: 'bg-indigo-600'
    },
    {
      icon: <GraduationCap className="w-12 h-12" />,
      title: 'Excise and Tax',
      description: 'Protection of worker rights and resolution of employment-related disputes.',
      link: '/services/labor-law',
      color: 'group-hover:bg-amber-600',
      bgColor: 'bg-amber-600'
    }
  ];

  return (
    <div>
      {/* Hero Section */}
      <div className="relative h-[400px] bg-gray-900">
        <div className="absolute inset-0 bg-gradient-to-r from-indigo-900 to-indigo-700 opacity-90"></div>
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmZmZmYiIGZpbGwtb3BhY2l0eT0iMC4wNCI+PHBhdGggZD0iTTM2IDM0djZoNnYtNmgtNnptNiA2djZoNnYtNmgtNnptLTEyIDBoNnY2aC02di02em0xMiAwaDZ2NmgtNnYtNnoiLz48cGF0aCBkPSJNMTIgMTJoNnY2aC02di02em02IDZoNnY2aC02di02em0wLTZoNnY2aC02di02em0xMiAwaDZ2NmgtNnYtNnptLTEyIDEyaDZ2NmgtNnYtNnptMTIgMGg2djZoLTZ2LTZ6Ii8+PC9nPjwvZz48L3N2Zz4=')] opacity-50"></div>
        <div className="relative container mx-auto px-4 h-full flex items-center">
          <div>
            <div className="inline-block bg-white/20 text-white px-4 py-1 rounded-md text-sm font-medium mb-4 backdrop-blur-sm">
              OUR EXPERTISE
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">Our Services</h1>
            <p className="text-gray-100 max-w-xl text-lg">
              Comprehensive legal solutions tailored to your specific needs with a commitment to excellence and results.
            </p>
          </div>
        </div>
      </div>

      {/* Services Grid */}
      <div className="container mx-auto px-4 py-20">
        <div className="text-center mb-16">
          <h2 className="text-3xl font-bold mb-4">Legal Expertise You Can Trust</h2>
          <p className="text-gray-600 max-w-2xl mx-auto">
            Our team provides expert legal representation across a wide range of practice areas, delivering personalized solutions for every client.
          </p>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {services.map((service, index) => (
            <ServiceCard
              key={index}
              icon={service.icon}
              title={service.title}
              description={service.description}
              link={service.link}
              color={service.color}
            />
          ))}
        </div>
      </div>

      {/* Additional Services CTA */}
      <div className="bg-gray-50 py-16">
        <div className="container mx-auto px-4">
          <div className="bg-indigo-700 rounded-xl p-8 md:p-12 text-center md:text-left md:flex items-center justify-between">
            <div className="mb-6 md:mb-0">
              <h3 className="text-2xl font-bold text-white mb-2">Need a Customized Legal Solution?</h3>
              <p className="text-indigo-100">
                Contact our team for a personalized consultation tailored to your specific legal needs.
              </p>
            </div>
            <Link 
              to="/contact" 
              className="inline-flex items-center px-6 py-3 rounded-lg bg-white text-indigo-700 font-medium transition-colors hover:bg-indigo-50"
            >
              Contact Us
              <ArrowRight className="ml-2 w-4 h-4" />
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Services;